/**
 * GPN Admin Portal - Dynamic Translation Service
 * Mirrors the dynamic translation tool from the React Native mobile app.
 * Provides on-the-fly translation for dynamic server data (user titles, prayer texts,
 * comments, announcements, descriptions) with multi-level caching and static fallback.
 */

import { getStaticText } from '../translations/staticTranslations';

class TranslationService {
  static cache = new Map();
  static STORAGE_KEY = 'gpn_dynamic_translation_cache';
  static MAX_PERSISTED_ENTRIES = 500;

  /**
   * Initialize cache from localStorage
   */
  static init() {
    try {
      const persisted = localStorage.getItem(this.STORAGE_KEY);
      if (persisted) {
        const parsed = JSON.parse(persisted);
        Object.entries(parsed).forEach(([k, v]) => {
          this.cache.set(k, v);
        });
      }
    } catch (e) {
      console.warn('[TranslationService] Failed to load persisted cache:', e);
    }
  }

  /**
   * Persist cache to localStorage with size limiting
   */
  static persistCache() {
    try {
      const entries = Array.from(this.cache.entries()).slice(-this.MAX_PERSISTED_ENTRIES);
      const obj = Object.fromEntries(entries);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      // Quota exceeded or error
    }
  }

  /**
   * Translates a string from source/auto to the target language.
   * @param {string} text Dynamic text to translate
   * @param {string} targetLangCode Target language code ('te', 'hi', 'ta', 'es', etc.)
   * @returns {Promise<string>} Translated text
   */
  static async translate(text, targetLangCode = 'en') {
    if (!text || typeof text !== 'string' || !text.trim()) {
      return text || '';
    }

    const target = (targetLangCode || 'en').toLowerCase().trim();
    const cleanText = text.trim();

    // 0. If text is pure numeric or special symbols, return original
    if (/^[\d\s\W]+$/.test(cleanText)) {
      return text;
    }

    // If target is English and text is already pure ASCII, return as is
    const isPureAscii = !/[^\u0020-\u007E\t\r\n]/.test(cleanText);
    if (target === 'en' && isPureAscii) {
      return text;
    }

    const cacheKey = `${target}:${cleanText}`;

    // 1. Check in-memory / persisted cache
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // 2. Quick check: Is this in our static dictionary?
    try {
      const staticResolved = getStaticText(target, cleanText, null);
      if (staticResolved && staticResolved !== cleanText) {
        this.cache.set(cacheKey, staticResolved);
        return staticResolved;
      }
    } catch (e) {
      // Continue to API translation
    }

    // Determine smart source language:
    // If target is non-English and text contains English/Latin characters, use sl='en' to force translating English words even in mixed text
    const hasLatin = /[a-zA-Z]/.test(cleanText);
    const sourceLang = target !== 'en' && hasLatin ? 'en' : 'auto';

    // 3. Multi-Engine Dynamic Translation with fallback
    const engines = [
      // Engine A: Google Client GTX (fast, reliable array format)
      async () => {
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${encodeURIComponent(target)}&dt=t&q=${encodeURIComponent(cleanText)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data) && Array.isArray(data[0])) {
          return data[0].map((chunk) => chunk[0] || '').join('').trim();
        }
        throw new Error('Invalid GTX response');
      },
      // Engine B: Google Client AT (reliable JSON format)
      async () => {
        const url = `https://translate.google.com/translate_a/single?client=at&dt=t&dj=1&sl=${sourceLang}&tl=${encodeURIComponent(target)}&q=${encodeURIComponent(cleanText)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data && Array.isArray(data.sentences) && data.sentences.length > 0) {
          return data.sentences.map((s) => s.trans || '').join('').trim();
        }
        throw new Error('Invalid AT response');
      },
      // Engine C: Google Clients5 Dict format
      async () => {
        const url = `https://clients5.google.com/translate_a/t?client=dict-chrome-ex&sl=${sourceLang}&tl=${encodeURIComponent(target)}&q=${encodeURIComponent(cleanText)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data) && Array.isArray(data[0]) && data[0][0]) {
          return String(data[0][0]).trim();
        }
        throw new Error('Invalid Clients5 response');
      },
    ];

    for (const engine of engines) {
      try {
        const res = await engine();
        if (res && res.length > 0) {
          this.cache.set(cacheKey, res);
          this.persistCache();
          return res;
        }
      } catch (err) {
        // Try next engine
      }
    }

    // Graceful fallback to original text
    return cleanText;
  }

  /**
   * Batch translate an array of texts
   */
  static async translateBatch(texts = [], targetLangCode = 'en') {
    if (!Array.isArray(texts) || !texts.length) return [];
    return Promise.all(texts.map((t) => this.translate(t, targetLangCode)));
  }

  /**
   * Translate HTML / rich text content while preserving markup
   */
  static async translateHtml(html = '', targetLangCode = 'en') {
    if (!html || typeof html !== 'string') return html || '';
    if (targetLangCode === 'en' || !html.includes('<')) {
      return this.translate(html, targetLangCode);
    }
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) {
        if (walker.currentNode.nodeValue && walker.currentNode.nodeValue.trim()) {
          textNodes.push(walker.currentNode);
        }
      }
      for (const node of textNodes) {
        const raw = node.nodeValue.trim();
        if (raw) {
          const trans = await this.translate(raw, targetLangCode);
          node.nodeValue = node.nodeValue.replace(raw, trans);
        }
      }
      return doc.body.innerHTML;
    } catch (e) {
      return this.translate(html, targetLangCode);
    }
  }

  /**
   * Clear all caches
   */
  static clearCache() {
    this.cache.clear();
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {}
  }
}

// Auto-initialize on import
TranslationService.init();

/**
 * Universal Native Value Setter for React 16/17/18 Controlled Components
 * Properly resets React's internal _valueTracker to prevent event suppression,
 * and dispatches native bubbling 'input' and 'change' events.
 */
export function setNativeValue(element, value) {
  if (!element || element.value === value) return;
  try {
    const previousValue = element.value;

    // Reset React's internal value tracker
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(previousValue + '_gpn_react_sync');
    }

    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

    if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, value);
    } else if (valueSetter) {
      valueSetter.call(element, value);
    } else {
      element.value = value;
    }
  } catch (e) {
    element.value = value;
  }

  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Get active translation language from form setting or admin global language
 */
export function getActiveFormLanguage() {
  try {
    const formLang = localStorage.getItem('gpn_admin_form_translate_lang');
    if (formLang && formLang !== 'auto_inherit') {
      return formLang;
    }
    const adminLang = localStorage.getItem('gpn_admin_language');
    if (adminLang && adminLang !== 'en') {
      return adminLang;
    }
    return formLang || 'en';
  } catch (e) {
    return 'en';
  }
}

/**
 * Set active form translation language and dispatch global sync event
 */
export function setActiveFormLanguage(langCode) {
  try {
    localStorage.setItem('gpn_admin_form_translate_lang', langCode);
    window.dispatchEvent(new CustomEvent('gpn_form_lang_changed', { detail: langCode }));
  } catch (e) {}
}

/**
 * Check if a DOM element is an eligible text field for auto-translation
 */
export function isEligibleField(el) {
  if (!el || el.hasAttribute('data-gpn-translator') || el.closest('[data-gpn-translator="true"]')) {
    return false;
  }
  const isRich = el.isContentEditable || el.classList?.contains('ql-editor');
  const isInput = el.tagName === 'INPUT' && (el.type === 'text' || !el.type);
  const isTextarea = el.tagName === 'TEXTAREA';

  if (!isRich && !isInput && !isTextarea) return false;
  if (el.readOnly || el.disabled) return false;

  const placeholder = (el.getAttribute('placeholder') || '').toLowerCase();
  const name = (el.getAttribute('name') || '').toLowerCase();
  const id = (el.getAttribute('id') || '').toLowerCase();
  const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();

  if (
    placeholder.includes('search') ||
    name.includes('search') ||
    id.includes('search') ||
    ariaLabel.includes('search')
  ) {
    return false;
  }
  if (name.includes('phone') || name.includes('email') || name.includes('password') || name.includes('url')) {
    return false;
  }
  return true;
}

export default TranslationService;
